// eslint-disable-next-line id-length
import _ from 'lodash';

export const FEES_LABELS = {
  rentalFee: 'Rental Fee',
  damageWaiverFee: 'Damage Protection (15%)',
  environmentalFee: 'Environmental Fee',
  deliveryFee: 'Delivery Fee',
  pickupFee: 'Pickup Fee',
  otherFees: 'Other Fee(s)'
};
export const TAX_LABELS = {
  rentalFee: 'Rental Period',
  damageWaiverFee: 'Damage Protection',
  environmentalFee: 'Environmental',
  deliveryFee: 'Delivery',
  pickupFee: 'Pickup',
  otherFees: 'Other'
};
export const DURATION_FEES = ['rental_fee', 'accessory_fee'];
export const OPTIONAL_FEES = ['damage_waiver_fee'];
export const TRACKED_FEES = ['delivery_fee', 'pickup_fee', 'environmental_fee'];

export const formatCurrency = (amount) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });

  return formatter.format(amount);
};

export const formatPercentage = (amount) => {
  // 3 decimals
  return `${Math.round((amount + Number.EPSILON) * 100000) / 1000}%`;
};

const groupFeeTypes = ({ fees, damageProtection = false, taxes = false }) => {
  return fees?.reduce((feeAmt, fee) => {
    const key = fee.type;

    // Rental Fees are ONLY tracked seperately as taxes
    // Damage Fee is ONLY rtracked if damageProtection is true
    if (
      (DURATION_FEES.includes(key) && !taxes)
      || (OPTIONAL_FEES.includes(key) && !damageProtection)
    ) {
      return feeAmt;
    }

    // Any Fee we explicitly track separately
    // Rental fees are ONLY tracked seperately as taxes
    // Damage Fee is ONLY tracked if damageProtection is true
    if (
      TRACKED_FEES.includes(key)
      || (DURATION_FEES.includes(key) && taxes)
      || (OPTIONAL_FEES.includes(key) && damageProtection)
    ) {
      return {
        ...feeAmt,
        [_.camelCase(key)]: (feeAmt[_.camelCase(key)] || 0) + (taxes ? fee.taxTotal : fee.value)
      };
    }

    // any fees that are not planned and tracked at the moment
    return {
      ...feeAmt,
      otherFees: (feeAmt.otherFees || 0) + (taxes ? fee.taxTotal : fee.value)
    };
  }, {}) || {};
};

const groupToolsFeeTypes = ({ tools, damageProtection = false, taxes = false }) => {
  return tools?.reduce((feeAmt, tool) => {
    const toolFees = groupFeeTypes({ fees: tool.fees, damageProtection, taxes });
    const temp = Object.keys(toolFees).reduce((acc, key) => {
      acc[key] = (acc[key] || 0) + toolFees[key];
      return acc;
    }, {});

    return { ...feeAmt, ...temp };
  }, {}) || {};
};

export const getHasDiscount = (estimateData) => {
  return Boolean(estimateData?.tools?.some((tool) => tool.discountedDeposit));
};

export const transformEstimateData = (estimate) => {
  if (!estimate) { return null; }

  const result = {
    fees: estimate?.fees || [],
    tools: estimate?.tools || [],
    totals: {
      totalDamageWaiver: 0,
      totalDamageWaiverTax: 0,
      totalDamageWaiverWithTax: 0,
      totalDeposit: 0,
      totalDiscountedDeposit: 0,
      totalEstimate: 0,
      totalEstimateTax: 0,
      totalEstimateWithoutDamageWaiver: 0,
      totalEstimateWithoutDamageWaiverTax: 0,
      totalEstimateWithoutDamageWaiverWithTax: 0,
      totalEstimateWithTax: 0,
      totalFees: 0,
      totalFeesTax: 0,
      totalFeesWithTax: 0,
      totalTools: 0,
      totalToolsTax: 0,
      totalToolsWithTax: 0,
      totalUnrackedFees: 0,
      totalUnrackedFeesTax: 0,
      totalUnrackedFeesWithTax: 0
    },
  };

  // Process fees
  result.fees.forEach((fee) => {
    result.totals.totalFees += fee.value || 0;
    result.totals.totalFeesTax += fee.taxTotal || 0;
    result.totals.totalFeesWithTax += (fee.value || 0) + (fee.taxTotal || 0);
  });

  // Process tools
  result.tools.forEach((tool) => {
    let toolFeeTotal = 0;
    let toolTaxTotal = 0;
    let damageWaiverFeeTotal = 0;
    let damageWaiverTaxTotal = 0;
    let otherFeeTotal = 0;
    let otherTaxTotal = 0;

    tool.fees.forEach((fee) => {
      if (DURATION_FEES.includes(fee.type)) {
        toolFeeTotal += fee.value || 0;
        toolTaxTotal += fee.taxTotal || 0;
      } else if (OPTIONAL_FEES.includes(fee.type)) {
        damageWaiverFeeTotal += fee.value || 0;
        damageWaiverTaxTotal += fee.taxTotal || 0;
      } else {
        // ANY tool fees that are displayed separately from duration related fees
        otherFeeTotal += fee.value || 0;
        otherTaxTotal += fee.taxTotal || 0;
      }
    });

    result.totals.totalTools += toolFeeTotal;
    result.totals.totalToolsTax += toolTaxTotal;
    result.totals.totalToolsWithTax += toolFeeTotal + toolTaxTotal;

    result.totals.totalDamageWaiver += damageWaiverFeeTotal;
    result.totals.totalDamageWaiverTax += damageWaiverTaxTotal;
    result.totals.totalDamageWaiverWithTax += damageWaiverFeeTotal + damageWaiverTaxTotal;

    result.totals.totalUnrackedFees += otherFeeTotal;
    result.totals.totalUnrackedFeesTax += otherTaxTotal;
    result.totals.totalUnrackedFeesWithTax += otherFeeTotal + otherTaxTotal;

    result.totals.totalDeposit += tool.deposit || 0;
    result.totals.totalDiscountedDeposit += tool.discountedDeposit || 0;
  });

  // Calculate total estimates
  result.totals.totalEstimate = (
    result.totals.totalFees
    + result.totals.totalTools
    + result.totals.totalDamageWaiver
    + result.totals.totalUnrackedFees
  );

  result.totals.totalEstimateTax = (
    result.totals.totalFeesTax
    + result.totals.totalToolsTax
    + result.totals.totalDamageWaiverTax
    + result.totals.totalUnrackedFeesTax
  );

  result.totals.totalEstimateWithTax = (
    result.totals.totalEstimate + result.totals.totalEstimateTax
  );

  result.totals.totalEstimateWithoutDamageWaiver = (
    result.totals.totalEstimate - result.totals.totalDamageWaiver
  );

  result.totals.totalEstimateWithoutDamageWaiverTax = (
    result.totals.totalEstimateTax
    - result.totals.totalDamageWaiverTax
  );

  result.totals.totalEstimateWithoutDamageWaiverWithTax = (
    result.totals.totalEstimateWithoutDamageWaiver
    + result.totals.totalEstimateWithoutDamageWaiverTax
  );

  return result;
};

export const getFeeBreakdown = ({ data, damageProtection = false }) => {
  if (_.isEmpty(data)) { return null; }

  let feeBreakdown = {};
  let taxBreakdown = {};

  taxBreakdown.rentalFee = data.totals?.totalToolsTax;

  if (damageProtection) {
    feeBreakdown.damageWaiverFee = data.totals?.totalDamageWaiver;
    taxBreakdown.damageWaiverFee = data.totals?.totalDamageWaiverTax;
  }

  feeBreakdown = {
    ...feeBreakdown,
    ...groupFeeTypes({ fees: data?.fees || [], damageProtection }),
    ...groupToolsFeeTypes({ tools: data?.tools || [], damageProtection })
  };
  taxBreakdown = {
    ...taxBreakdown,
    ...groupFeeTypes({ fees: data?.fees || [], damageProtection, taxes: true }),
    ...groupToolsFeeTypes({ tools: data?.tools || [], damageProtection, taxes: true })
  };

  return {
    fees: Object.keys(feeBreakdown).map((key) => ({
      name: FEES_LABELS[key],
      cost: feeBreakdown[key]
    })).sort((first, second) => first?.name?.localeCompare(second?.name)),
    taxes: Object.keys(taxBreakdown).map((key) => ({
      name: TAX_LABELS[key],
      cost: taxBreakdown[key]
    })).sort((first, second) => first?.name?.localeCompare(second?.name)),
  };
};

export const getDamageWaiverFee = (estimateData) => {
  return estimateData?.tools?.reduce((totalDamageFee, tool) => {

    const toolFeeTotal = tool?.fees?.reduce((feeAmt, fee) => {
      const key = fee.type;
      if (key === 'damage_waiver_fee') {
        return { ...feeAmt, damageWaiverFee: fee.value + (feeAmt.damageWaiverFee || 0) };
      }

      return {
        ...feeAmt
      };
    }, {}) || {};

    let temp = {};
    Object.keys(toolFeeTotal).forEach((key) => {
      temp[key] = toolFeeTotal[key] + (totalDamageFee[key] || 0);
    });

    return {
      ...totalDamageFee,
      ...temp
    };
  }, {}) || {};
};

export const getDamageWaiverPercentage = (estimateData) => {
  if (!estimateData?.tools || estimateData?.tools.length === 0) {
    return 0;
  }
  return estimateData?.tools[0]?.fees?.filter((fee) => fee.type === 'damage_waiver_fee')[0]?.percentage;
};

export const getSalesTaxPercentage = (estimateData) => {
  if (!estimateData?.tools || estimateData?.tools.length === 0) {
    return 0;
  }
  return estimateData?.tools[0]?.taxPercentage;
};
